import React from 'react';
import type { Course } from '../types/course';
import { useData } from '../context/DataContext';

interface CourseFormProps {
  course: Course;
  onUpdate: (course: Course) => void;
}

export function CourseForm({ course, onUpdate }: CourseFormProps) {
  const { settings } = useData();

  const handleCategoryToggle = (category: string) => {
    const currentCategories = course.categories || [];
    const newCategories = currentCategories.includes(category)
      ? currentCategories.filter(c => c !== category)
      : [...currentCategories, category];
    onUpdate({ ...course, categories: newCategories });
  };

  const handleTagToggle = (tag: string) => {
    const currentTags = course.tags || [];
    const newTags = currentTags.includes(tag)
      ? currentTags.filter(t => t !== tag)
      : [...currentTags, tag];
    onUpdate({ ...course, tags: newTags });
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <label className="block text-sm font-medium mb-2">Titre</label>
        <input
          type="text"
          value={course.titre || ''}
          onChange={(e) => onUpdate({ ...course, titre: e.target.value })}
          className="w-full p-2 border rounded"
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">Image URL</label>
        <input
          type="text"
          value={course.image || ''}
          onChange={(e) => onUpdate({ ...course, image: e.target.value })}
          className="w-full p-2 border rounded"
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">Note (sur 5)</label>
        <input
          type="number"
          value={course.rating || 5}
          onChange={(e) => onUpdate({ ...course, rating: Math.min(5, Math.max(0, parseFloat(e.target.value) || 0)) })}
          className="w-full p-2 border rounded"
          step="0.5"
          min="0"
          max="5"
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">Prix normal (€)</label>
        <div className="flex items-center gap-2">
          <input
            type="number"
            value={course.prix || 0}
            onChange={(e) => onUpdate({ ...course, prix: parseFloat(e.target.value) || 0 })}
            className="w-full p-2 border rounded"
            step="0.01"
            min="0"
          />
          <label className="flex items-center gap-1">
            <input
              type="checkbox"
              checked={course.isMonthly}
              onChange={(e) => onUpdate({ ...course, isMonthly: e.target.checked })}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-600">Prix mensuel</span>
          </label>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">Prix promotionnel (€)</label>
        <input
          type="number"
          value={course.prixPromo ?? ''}
          onChange={(e) => {
            const value = e.target.value;
            onUpdate({ ...course, prixPromo: value === '' ? undefined : parseFloat(value) });
          }}
          className="w-full p-2 border rounded"
          step="0.01"
          min="0"
          placeholder="Laissez vide pour désactiver la promo"
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">Niveau</label>
        <select
          value={course.niveau}
          onChange={(e) => onUpdate({ ...course, niveau: e.target.value as Course['niveau'] })}
          className="w-full p-2 border rounded"
        >
          <option value="Débutant">Débutant</option>
          <option value="Intermédiaire">Intermédiaire</option>
          <option value="Avancé">Avancé</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">Lien</label>
        <input
          type="text"
          value={course.lien || ''}
          onChange={(e) => onUpdate({ ...course, lien: e.target.value })}
          className="w-full p-2 border rounded"
        />
      </div>

      <div className="md:col-span-2">
        <label className="block text-sm font-medium mb-2">Description</label>
        <textarea
          value={course.description || ''}
          onChange={(e) => onUpdate({ ...course, description: e.target.value })}
          className="w-full p-2 border rounded"
          rows={3}
        />
      </div>

      <div className="md:col-span-2">
        <label className="block text-sm font-medium mb-2">Catégories</label>
        <div className="flex flex-wrap gap-2">
          {settings.categories.map((category) => (
            <button
              key={category}
              onClick={() => handleCategoryToggle(category)}
              className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium transition-colors duration-200 ${
                (course.categories || []).includes(category)
                  ? 'bg-red-500 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              {category}
            </button>
          ))}
        </div>
      </div>

      <div className="md:col-span-2">
        <label className="block text-sm font-medium mb-2">Tags</label>
        <div className="flex flex-wrap gap-2">
          {settings.tags.map((tag) => (
            <button
              key={tag}
              onClick={() => handleTagToggle(tag)}
              className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium transition-colors duration-200 ${
                (course.tags || []).includes(tag)
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              {tag}
            </button>
          ))}
        </div>
      </div>

      <div className="md:col-span-2 flex gap-4">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={course.hasVideo || false}
            onChange={(e) => onUpdate({ ...course, hasVideo: e.target.checked })}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm text-gray-700">Formation vidéo</span>
        </label>
        
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={course.hasLiveClasses || false}
            onChange={(e) => onUpdate({ ...course, hasLiveClasses: e.target.checked })}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm text-gray-700">Classes en direct</span>
        </label>
      </div>
    </div>
  );
}